import { splitUrl } from "@repo/fika-shared/src/lib/slug"

import { useParams } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { createMemo, createSignal, Show } from "solid-js"
import { ulid } from "ulid"
import { Box } from "@/designSystem/components/box"
import { Icon } from "@/designSystem/components/icon"
import { Mini } from "@/designSystem/components/mini/mini"
import { Text } from "@/designSystem/components/text"
import { NoteFragment } from "@/designSystem/patterns/note"
import { RightPanel } from "@/designSystem/patterns/right-panel"
import { createFragment, graphql, type ResultOf, readFragment } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { defaultJsonContent } from "@/lib/default-json-content"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { usePostEditor } from "@/pages/posts/post-editor-context"
import { createBlogRouter } from "@/routes/posts-router"
import { MasonryNoteFragment, MasonrySection } from "./masonry-section"
import {
  emptyStateActionsClass,
  emptyStateClass,
  emptyStateContainerClass,
} from "./post-notes.css"

const PostForNotesFragment = graphql(
  `fragment PostForNotesFragment on Post {
    id
    relatedNotes {
      id
      ...MasonryNoteFragment
    }
  }`,
  [MasonryNoteFragment],
)

const PostQuery = graphql(
  `query PostForNotes($id: String!) {
    post(id: $id) {
      id
      ...PostForNotesFragment
    }
  }`,
  [PostForNotesFragment],
)

const DeleteNoteMutation = graphql(
  `mutation DeleteNoteFromPostPanel($id: ID!) {
    deleteNote(id: $id) {
      ...on MutationDeleteNoteSuccess {
        data
      }
      ...ErrorFragment
    }
  }`,
  [ErrorFragment],
)

const CreateNoteMutation = graphql(
  `mutation CreateNoteFromPostPanel($id: ID!, $content: String!, $type: NoteTypeEnum, $postId: ID) {
    createNote(id: $id, content: $content, type: $type, postId: $postId) {
      ...on MutationCreateNoteSuccess {
        data {
          id
          ...MasonryNoteFragment
          posts {
            ...PostForNotesFragment
          }
        }
      }
      ...ErrorFragment
    }
  }`,
  [ErrorFragment, MasonryNoteFragment, PostForNotesFragment],
)

export function PostNotes() {
  const params = useParams()
  const id = createMemo(() => splitUrl(params.postId!) || "")
  const { LL } = useI18nContext()

  const [queryResult] = createQuery({
    query: PostQuery,
    variables: createMemo(() => ({ id: id() })),
    pause: () => !id(),
  })

  const post = createMemo(() => queryResult.data?.post)

  return (
    <Show
      when={!queryResult.fetching && post()}
      fallback={<div>{LL().common.loading()}</div>}
    >
      {p => <SafePostNotes post={p()} />}
    </Show>
  )
}

interface PostSidebarContentProps {
  post: NonNullable<ResultOf<typeof PostQuery>["post"]>
}

function SafePostNotes(props: PostSidebarContentProps) {
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()
  const { editor } = usePostEditor()
  const [editingNoteId, setEditingNoteId] = createSignal<string | null>(null)

  const [, createNote] = createMutationWithErrorHandling(
    CreateNoteMutation,
    "createNote",
  )
  const [, deleteNote] = createMutationWithErrorHandling(
    DeleteNoteMutation,
    "deleteNote",
  )
  const postFragment = createFragment(PostForNotesFragment, () => props.post)
  const relatedNotes = createMemo(() => postFragment()?.relatedNotes ?? [])

  function handleCreateNote() {
    const noteId = ulid()
    setEditingNoteId(noteId)
    createNote({
      id: noteId,
      content: defaultJsonContent,
      postId: props.post.id,
    })
  }

  function handleDeleteNote(noteId: string) {
    deleteNote({ id: noteId })
  }

  function handleNoteClick(noteId: string) {
    setEditingNoteId(noteId)
  }

  function handleCopyToPost(noteContent: string) {
    const editorInstance = editor()
    if (!editorInstance) return

    try {
      const parsed = JSON.parse(noteContent)
      const blocks = parsed.content ?? []
      editorInstance.chain().focus().insertContent(blocks).run()
    } catch {
      // ignore malformed content
    }
  }

  const hasNotes = createMemo(() => relatedNotes().length > 0)

  const masonryItems = createMemo(() => {
    const items = relatedNotes().map(note =>
      readFragment(MasonryNoteFragment, note),
    )
    return items.toSorted((a, b) => {
      const aDate = readFragment(NoteFragment, a).createdAt
      const bDate = readFragment(NoteFragment, b).createdAt
      return new Date(bDate).getTime() - new Date(aDate).getTime()
    })
  })

  return (
    <RightPanel
      closeHref={paths().post().url}
      header={{
        type: "custom",
        content: (
          <Box
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box gap="8" flexDirection="row" alignItems="center">
              <Text fontSize="m" color="sand800" fontWeight="600">
                {LL().post.notes()}
              </Text>
            </Box>
            <Show when={hasNotes()}>
              <Box gap="4" flexDirection="row" alignItems="center">
                <Mini.Button
                  iconName="pen-tool"
                  onClick={handleCreateNote}
                  title={LL().notes.newNote()}
                />
              </Box>
            </Show>
          </Box>
        ),
      }}
    >
      <Show
        when={masonryItems().length > 0}
        fallback={
          <div class={emptyStateContainerClass}>
            <Box
              gap="12"
              alignItems="center"
              padding="24"
              class={emptyStateClass}
            >
              <Icon name="sticky-note" size="32" color="sand400" />
              <Text color="sand500" fontSize="s" textAlign="center">
                {LL().notes.noPinnedYet()}
              </Text>
            </Box>
            <div class={emptyStateActionsClass}>
              <Mini.Button
                iconName="pen-tool"
                onClick={handleCreateNote}
                title={LL().notes.newNote()}
              />
            </div>
          </div>
        }
      >
        <MasonrySection
          items={() => masonryItems()}
          showDelete={true}
          onDelete={handleDeleteNote}
          editingNoteId={editingNoteId}
          onNoteClick={handleNoteClick}
          onCopyToPost={handleCopyToPost}
        />
      </Show>
    </RightPanel>
  )
}
